import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  costFormater,
  InvestmentsLiquidationPending,
  NewDonutChart,
  Preloader,
  Responsive,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { RiskControlOptions, Roles, TBD_TITLE } from '@/common/constants';
import { useHasAccess, useHistory } from '@/modules/core/hooks';

import { useInvestmentsHoldings, useInvestor, useRiskModels } from '../hooks';
import { InvestmentPaths } from '../investments.types';
import { useInvestmentsProcessingSnapshot } from '../NewInvestments/hook';
import InvestmentDonutGroups from '../NewInvestments/InvestmentsBanner/InvestmentDonut/InvestmentDonutGroups';

const HoverWrapper = styled(Box)`
  position: absolute;
  right: 100%;
  display: none;
  z-index: 110;
`;

const Wrapper = styled(Box)`
  &:hover {
    ${HoverWrapper} {
      display: inline-block;
    }
  }
`;
interface Props {
  accountId: string;
  disabled?: boolean;
}

const InvestmentManagementDonut: React.FC<Props> = ({ accountId, disabled }) => {
  const { isMobile } = useResponsive();
  const history = useHistory();
  const {
    data: { currentToleranceModel, type: investmentType, isFullLiquidationInProgress },
  } = useInvestor(accountId);
  const {
    isHoldingLoading,
    formattedByCategory,
    formattedByCategoryCut,
    isSuccessLoaded,
    details: { portfolioBalance },
  } = useInvestmentsHoldings(accountId);
  const { t } = useTranslation();
  const { currentShortOption } = useRiskModels(currentToleranceModel as RiskControlOptions);
  const isLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const hintLessCategories = useMemo(() => {
    return formattedByCategoryCut.map((item) => ({
      ...item,
      hint: undefined,
    }));
  }, [formattedByCategoryCut]);

  const handleRedirect = useCallback(() => {
    window.scrollTo({
      top: 0,
    });
    history.push(`${ROUTES.INVESTMENTS}?id=${accountId}`);
  }, [accountId, history]);
  const isSnapshotShowed = useInvestmentsProcessingSnapshot();

  const smallDonutNode = useMemo(() => {
    if (isSnapshotShowed) {
      return (
        <Box direction="row" align="center" justify="center">
          <Box
            round="50%"
            direction="row"
            align="center"
            justify="center"
            width="64px"
            height="64px"
            background="border1"
          >
            <Box
              direction="row"
              align="center"
              round="50%"
              justify="center"
              width="46px"
              height="46px"
              background="canvas"
            />
          </Box>
        </Box>
      );
    }
    return (
      <NewDonutChart
        gap={0.005}
        round={2}
        testId="investment"
        data={hintLessCategories}
        strokeWidth={9}
        size={60}
      />
    );
  }, [hintLessCategories, isSnapshotShowed]);

  const titleNode = useMemo(() => {
    return (
      <Box width={{ max: '90px' }} align="center">
        {investmentType === InvestmentPaths.MANAGED ? (
          <>
            <Text weight={700} color="textBody" size="medium">
              {t('Managed')}
            </Text>
            <Text textAlign="center" color="textDisabled" size="small" weight={500}>
              {currentShortOption?.label}
            </Text>
          </>
        ) : investmentType === InvestmentPaths.SELF_DIRECTED ? (
          <Box width={{ max: '88px' }}>
            <Text weight={700} textAlign="center" color="textBody" size="medium">
              {t('Self Directed')}
            </Text>
          </Box>
        ) : null}
      </Box>
    );
  }, [currentShortOption?.label, investmentType, t]);
  const donutNode = useMemo(() => {
    if (isSnapshotShowed) {
      return (
        <Box direction="row" align="center" justify="center" margin={{ bottom: 'spacing12' }}>
          <Box
            round="50%"
            direction="row"
            align="center"
            justify="center"
            width="152px"
            height="152px"
            background="border1"
          >
            <Box
              direction="row"
              align="center"
              round="50%"
              justify="center"
              width="112px"
              height="112px"
              background="canvas"
            >
              {titleNode}
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <NewDonutChart testId="investment" data={formattedByCategoryCut} size={166}>
        {titleNode}
      </NewDonutChart>
    );
  }, [formattedByCategoryCut, isSnapshotShowed, titleNode]);
  return (
    <Box
      direction={isMobile ? 'column' : 'row'}
      round="moduleRound"
      background="canvas"
      align={isMobile ? 'start' : 'center'}
      fill
      justify="between"
      data-testid="investment-small-donut"
      pad={{ horizontal: 'spacing24', vertical: isMobile ? 'spacing24' : undefined }}
    >
      <Box direction="row" align="center">
        <Wrapper align="center" justify="center" style={{ position: 'relative' }}>
          {isHoldingLoading && !isSuccessLoaded ? (
            <Preloader size={50} />
          ) : (
            <>
              {isFullLiquidationInProgress ? (
                <InvestmentsLiquidationPending width="46px" height="46px" />
              ) : (
                smallDonutNode
              )}
            </>
          )}
          {!isFullLiquidationInProgress && !isMobile && (
            <HoverWrapper
              background="canvas"
              round="container1Round"
              pad="m"
              width={{ min: '236px' }}
              elevation="active"
            >
              {donutNode}
              {!isSnapshotShowed && <InvestmentDonutGroups isSmallMode value={formattedByCategory} />}

              <Box margin={{ top: 'm' }} align="center" justify="center">
                <AppButton
                  disabled={isLimitedAccess || disabled}
                  testId="investment-donut_manage-investments_circle"
                  onClick={handleRedirect}
                >
                  {t('Manage Investments')}
                </AppButton>
              </Box>
            </HoverWrapper>
          )}
        </Wrapper>
        <Box direction="column" margin={{ left: 'm' }}>
          {isFullLiquidationInProgress ? (
            <Text size="large" margin={{ right: 'xl' }} style={{ lineHeight: '22px' }} weight={700}>
              {t('Your full portfolio liquidation is in progress')}
            </Text>
          ) : (
            <>
              <Text size="large" weight={700}>
                {isSnapshotShowed
                  ? TBD_TITLE
                  : costFormater(portfolioBalance, {
                      isPrecision: true,
                    })}
              </Text>
              <Text color="textSecondary" weight={400}>
                {investmentType === InvestmentPaths.MANAGED ? t('Managed') : t('Self Directed')}
              </Text>
            </>
          )}
        </Box>
      </Box>
      <Responsive size={Responsive.Mobile}>
        <Box fill margin={{ top: 'spacing8' }}>
          <AppButton
            fill
            testId="investment-donut_manage-investments"
            disabled={isLimitedAccess || disabled}
            onClick={handleRedirect}
          >
            {t('Manage Investments')}
          </AppButton>
        </Box>
      </Responsive>
      <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
        <div>
          <Box>
            <AppButton
              testId="investment-donut_manage-investments"
              disabled={isLimitedAccess || disabled}
              onClick={handleRedirect}
            >
              {t('Manage Investments')}
            </AppButton>
          </Box>
        </div>
      </Responsive>
    </Box>
  );
};

export default InvestmentManagementDonut;
